import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Breakpoints, Color, Grid, Type } from '../../app/StyleGuide';
import { TrainingPoster } from '../../components/TrainingElements';
import WhiteLabel from '../../filters/WhiteLabel';
import pluralFilter from '../../filters/plural-filter';
import trainingService from '../../services/trainingService';
import CourseInformation from './CourseInformation';
import CourseMenu from './CourseMenu';
import CourseSteps from './CourseSteps';
import { useSteps } from './StepsContext';
import TrainingOverview from './TrainingOverview';
import { type Course } from './models/course';
import { type Pathway, type PathwayCourse } from './models/pathway';

const Aside = styled.aside`
  background-color: ${Color.Gray._10};
  position: fixed;
  top: 0;
  right: 0;
  width: 100vw;
  max-width: 450px;
  height: 100vh;
  transition: all 0.2s ease-in-out;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 500;
  transform: translateX(100%);
  @media screen and (min-width: ${Breakpoints.screen_md}) {
    position: static;
    flex: 0 0 25%;
    transform: none;
    width: inherit;
  }
  &.visible {
    transform: translateX(0);
  }
`;

const TrainingInformation = styled.div`
  padding: ${Grid._4};
  color: ${Type.Color.dark};
  h3 {
    font-weight: ${Type.Weight.bold};
    margin-top: 0;
  }
  p {
    color: ${Type.Color.medium};
  }
  .tl-progress-bar {
    margin-top: ${Grid._4};
  }
`;

const Tabs = styled.ul`
  margin: 0;
  padding: 0;
  display: flex;
  list-style: none;
  border-bottom: 1px solid ${Color.Gray._30};
  li {
    flex: 1 0 0;
    padding: ${Grid._4};
    text-align: center;
    font-size: ${Type.Scale._3};
    color: ${Type.Color.medium};
    cursor: pointer;
    &:hover {
      border-bottom: 3px solid ${Color.Gray._30};
    }
  }
  .active-training-menu-tab {
    border-bottom: 3px solid ${Color.Primary._50};
    color: ${Type.Color.dark};
    font-weight: ${Type.Weight.bold};
  }
`;

const StickyContent = styled.div`
  background: ${Color.Gray._10};
  box-shadow: inset 1px 0 rgba(0, 0, 0, 0.05);
  h3 {
    margin-top: 0;
  }
`;

const ScrollContent = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding-bottom: ${Grid._13};

  @media screen and (min-width: ${Breakpoints.screen_lg}) {
    padding-bottom: 0;
  }
`;

const CloseButton = styled.i`
  position: absolute;
  top: 0;
  right: 0;
  padding: 5px 10px;
  color: ${Color.Gray._50};

  @media screen and (min-width: ${Breakpoints.screen_md}) {
    display: none;
  }
`;

const TrainingPosterContainer = styled.div`
  background: ${Color.Gray._20};
  width: 100%;
  aspect-ratio: 16/9;

  ${TrainingPoster} {
    border-radius: 0;
  }
`;

const CourseList = ({
  training,
  currentCourse,
  isCourseExpanded,
  toggleIsCourseExpanded,
}: {
  training: any;
  currentCourse: any;
  isCourseExpanded: any;
  toggleIsCourseExpanded: any;
}) =>
  training.courses.map((course: any, i: number) => {
    const isCurrentCourse = course.id === currentCourse.id;
    return (
      <CourseMenu
        key={course.id}
        training={training}
        course={isCurrentCourse ? currentCourse : course}
        isCurrentCourse={isCurrentCourse}
        courseIndex={i}
        isCourseExpanded={isCourseExpanded[i]}
        toggleIsCourseExpanded={toggleIsCourseExpanded(i)}
      />
    );
  });

const PathwayHeader = ({ pathway }: { pathway: Pathway }) => {
  return (
    <>
      <h3 data-qa-hook="trainingName">{pathway.workflowName}</h3>
      <p>
        {pathway.progress.steps.completed +
          ' of ' +
          pathway.progress.steps.total +
          ' ' +
          pluralFilter(WhiteLabel.labels.course) +
          ' complete'}
      </p>
    </>
  );
};

const CourseHeader = ({ course, numCompletedSteps }: { course: Course; numCompletedSteps: number }) => {
  return (
    <>
      <h3 data-qa-hook="trainingName">{course.name}</h3>
      <CourseInformation
        course={course}
        numCompletedSteps={numCompletedSteps}
        progress={course.progress ? course.progress * 100 : 0}
      />
    </>
  );
};

type TrainingMenuProps = {
  close: () => void;
};

type IsCourseExpandedState = Record<number, boolean>;

type UseStepsResponse = {
  numCompletedSteps: number;
  currentStepIndex: number;
  training: Pathway | Course;
  isMobileTrainingMenuOpen: boolean;
  isWorkflow: boolean;
  currentCourse: Course | PathwayCourse;
  currentCourseIndex: number;
};

export default function TrainingMenu({ close }: TrainingMenuProps) {
  const [
    {
      numCompletedSteps,
      currentStepIndex,
      training,
      isMobileTrainingMenuOpen,
      isWorkflow,
      currentCourse,
      currentCourseIndex,
    },
  ] = useSteps() as [UseStepsResponse];
  const menuClass = isMobileTrainingMenuOpen ? 'visible' : '';
  const [showCourseMenu, setShowCourseMenu] = useState<boolean>(true);

  const autoScrollMenu = () => {
    const menu = document.querySelector('aside.training-menu .training-menu-scroll-content');
    const selectedStep = menu?.querySelector('li.course-menu-active') as HTMLElement | null;
    selectedStep?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const [isCourseExpanded, setIsCourseExpanded] = useState<IsCourseExpandedState>({});

  const toggleIsCourseExpanded = (courseIndex: number) => () => {
    setIsCourseExpanded(s => {
      return { ...s, [courseIndex]: !s[courseIndex] };
    });
  };

  useEffect(() => {
    if (!isCourseExpanded[currentCourseIndex]) {
      toggleIsCourseExpanded(currentCourseIndex)();
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        autoScrollMenu();
      });
    });
  }, [currentStepIndex, currentCourseIndex, showCourseMenu]);

  return (
    <Aside data-qa-hook="trainingSlideMenu" className={`training-menu ${menuClass}`}>
      {training.posterImage && (
        <TrainingPosterContainer>
          <TrainingPoster src={trainingService.getTrainingImageUrl(training.posterImage)} />
        </TrainingPosterContainer>
      )}
      <StickyContent className="training-header">
        <CloseButton data-qa-hook="closeSlideMenu" className="icon ion-close" onClick={close} />
        <TrainingInformation>
          {isWorkflow ? (
            <PathwayHeader pathway={training as Pathway} />
          ) : (
            <CourseHeader course={training as Course} numCompletedSteps={numCompletedSteps} />
          )}
        </TrainingInformation>
        <Tabs>
          <li
            data-qa-hook="contentsTab"
            className={showCourseMenu ? 'active-training-menu-tab' : ''}
            onClick={() => setShowCourseMenu(true)}
          >
            Contents
          </li>
          <li
            data-qa-hook="overviewTab"
            className={showCourseMenu ? '' : 'active-training-menu-tab'}
            onClick={() => setShowCourseMenu(false)}
          >
            Overview
          </li>
        </Tabs>
      </StickyContent>
      <ScrollContent className="training-menu-scroll-content">
        <div style={showCourseMenu ? {} : { display: 'none' }}>
          {isWorkflow ? (
            <CourseList
              training={training}
              currentCourse={currentCourse}
              isCourseExpanded={isCourseExpanded}
              toggleIsCourseExpanded={toggleIsCourseExpanded}
            />
          ) : (
            <CourseSteps course={currentCourse} isCurrentCourse={true} courseIndex={0} />
          )}
        </div>
        <div style={showCourseMenu ? { display: 'none' } : {}}>
          <TrainingOverview />
        </div>
      </ScrollContent>
    </Aside>
  );
}
